/*
 * Scenes API — thin wrapper around /v2/* routes. Each PATCH carries `version` and
 * surfaces 409s for the caller to reconcile. Phase 1 stubs only — Phase 2 fills in.
 */

import type { Scene, SceneImage, SceneVideo } from '@/types';
import { isLocalMode } from '@/local';
import { apiFetch } from './apiBase';
import {
  localCreateScene,
  localDeleteImage,
  localDeleteScene,
  localDeleteVideo,
  localGenerateImage,
  localGenerateVideo,
  localListScenes,
  localPatchScene,
} from '@/local/localBackend';

export class StaleVersionError extends Error {
  constructor(
    public readonly currentRow: unknown,
    message = 'Stale version — server has a newer copy.',
  ) {
    super(message);
    this.name = 'StaleVersionError';
  }
}

const JSON_HEADERS = { 'Content-Type': 'application/json' };

export async function listScenes(episodeUuid: string): Promise<Scene[]> {
  if (isLocalMode()) return localListScenes(episodeUuid);
  const res = await apiFetch(`/api/episode/${encodeURIComponent(episodeUuid)}/v2/scenes`);
  if (!res.ok) throw new Error(`listScenes failed: ${res.status}`);
  return res.json();
}

export async function patchScene(
  episodeUuid: string,
  id: string,
  fields: Partial<Pick<Scene, 'prompt' | 'order' | 'references' | 'heroImageId' | 'status'>>,
  version: number,
): Promise<Scene> {
  if (isLocalMode()) return localPatchScene(episodeUuid, id, fields, version);
  const res = await apiFetch(
    `/api/episode/${encodeURIComponent(episodeUuid)}/v2/scenes/${encodeURIComponent(id)}`,
    {
      method: 'PATCH',
      headers: JSON_HEADERS,
      body: JSON.stringify({ ...fields, version }),
    },
  );
  if (res.status === 409) {
    const current = await res.json();
    throw new StaleVersionError(current);
  }
  if (!res.ok) throw new Error(`patchScene failed: ${res.status}`);
  return res.json();
}

export async function createScene(
  episodeUuid: string,
  fields: Pick<Scene, 'prompt' | 'order' | 'references'>,
): Promise<Scene> {
  if (isLocalMode()) return localCreateScene(episodeUuid, fields);
  const res = await apiFetch(`/api/episode/${encodeURIComponent(episodeUuid)}/v2/scenes`, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(fields),
  });
  if (!res.ok) throw new Error(`createScene failed: ${res.status}`);
  return res.json();
}

export async function deleteScene(
  episodeUuid: string,
  id: string,
  version: number,
): Promise<void> {
  if (isLocalMode()) return localDeleteScene(episodeUuid, id, version);
  const res = await apiFetch(
    `/api/episode/${encodeURIComponent(episodeUuid)}/v2/scenes/${encodeURIComponent(id)}`,
    {
      method: 'DELETE',
      headers: JSON_HEADERS,
      body: JSON.stringify({ version }),
    },
  );
  if (res.status === 409) {
    const current = await res.json();
    throw new StaleVersionError(current);
  }
  if (!res.ok) throw new Error(`deleteScene failed: ${res.status}`);
}

export async function deleteSceneImage(
  episodeUuid: string,
  sceneId: string,
  imageId: string,
  version: number,
): Promise<void> {
  if (isLocalMode()) return localDeleteImage(episodeUuid, sceneId, imageId, version);
  const res = await apiFetch(
    `/api/episode/${encodeURIComponent(episodeUuid)}/v2/scenes/${encodeURIComponent(sceneId)}/images/${encodeURIComponent(imageId)}`,
    {
      method: 'DELETE',
      headers: JSON_HEADERS,
      body: JSON.stringify({ version }),
    },
  );
  if (res.status === 409) {
    const current = await res.json();
    throw new StaleVersionError(current);
  }
  if (!res.ok) throw new Error(`deleteSceneImage failed: ${res.status}`);
}

export async function deleteSceneVideo(
  episodeUuid: string,
  sceneId: string,
  videoId: string,
  version: number,
): Promise<void> {
  if (isLocalMode()) return localDeleteVideo(episodeUuid, sceneId, videoId, version);
  const res = await apiFetch(
    `/api/episode/${encodeURIComponent(episodeUuid)}/v2/scenes/${encodeURIComponent(sceneId)}/videos/${encodeURIComponent(videoId)}`,
    {
      method: 'DELETE',
      headers: JSON_HEADERS,
      body: JSON.stringify({ version }),
    },
  );
  if (res.status === 409) {
    const current = await res.json();
    throw new StaleVersionError(current);
  }
  if (!res.ok) throw new Error(`deleteSceneVideo failed: ${res.status}`);
}

export async function generateSceneImage(
  episodeUuid: string,
  sceneId: string,
  body: {
    prompt: string;
    references: { sceneIds: string[]; castIds: string[] };
    fresh: boolean;
  },
): Promise<SceneImage> {
  if (isLocalMode()) return localGenerateImage(episodeUuid, sceneId, body);
  const res = await apiFetch(
    `/api/episode/${encodeURIComponent(episodeUuid)}/v2/scenes/${encodeURIComponent(sceneId)}/images`,
    {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(body),
    },
  );
  if (!res.ok) throw new Error(`generateSceneImage failed: ${res.status}`);
  return res.json();
}

export async function generateSceneVideo(
  episodeUuid: string,
  sceneId: string,
  body: {
    sourceImageId: string;
    prompt: string;
    duration: 4 | 6 | 8;
    model: 'veo-fast' | 'veo-full';
    extendsVideoId?: string;
  },
): Promise<SceneVideo> {
  if (isLocalMode()) return localGenerateVideo(episodeUuid, sceneId, body);
  const res = await apiFetch(
    `/api/episode/${encodeURIComponent(episodeUuid)}/v2/scenes/${encodeURIComponent(sceneId)}/videos`,
    {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(body),
    },
  );
  if (!res.ok) throw new Error(`generateSceneVideo failed: ${res.status}`);
  return res.json();
}
